import React, { useState, useEffect } from 'react';
import {
	Card,
	CardContent,
	Typography,
	Button,
	CircularProgress,
	Alert,
	Box,
	Grid,
	Paper,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	ListItemIcon,
	TextField,
	Chip,
	Divider,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Autocomplete,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';
import { Title, useNotify, useDataProvider } from 'react-admin';
import { MCPTool, getMCPTools, callMCPTool, MCPToolCallResult } from '../../Services/quantiAI';

interface Project {
	id: number;
	folderId: string;
	name: string;
}

export const MCPTestPage = () => {
	const [tools, setTools] = useState<MCPTool[]>([]);
	const [selectedTool, setSelectedTool] = useState<MCPTool | null>(null);
	const [loading, setLoading] = useState(true);
	const [executing, setExecuting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [result, setResult] = useState<MCPToolCallResult | null>(null);
	const [args, setArgs] = useState<Record<string, any>>({});
	const [projects, setProjects] = useState<Project[]>([]);
	const [selectedProjectId, setSelectedProjectId] = useState<string>('');
	const notify = useNotify();
	const dataProvider = useDataProvider();

	// Charger les tools et projets au montage
	useEffect(() => {
		loadTools();
		loadProjects();
	}, []);

	const loadTools = async () => {
		try {
			setLoading(true);
			const data = await getMCPTools();
			// Trier par nom
			const sortedTools = (data.tools || []).sort((a, b) => a.name.localeCompare(b.name));
			setTools(sortedTools);
		} catch (err: any) {
			setError(err.message || 'Erreur lors du chargement des tools');
		} finally {
			setLoading(false);
		}
	};

	const loadProjects = async () => {
		try {
			const { data } = await dataProvider.getList('projects', {
				pagination: { page: 1, perPage: 500 },
				sort: { field: 'name', order: 'ASC' },
				filter: {},
			});
			setProjects(data as Project[]);
		} catch (err) {
			console.error('Failed to load projects:', err);
		}
	};

	// Vérifie si le tool sélectionné requiert un project_id
	const toolRequiresProject = selectedTool?.inputSchema?.properties?.project_id !== undefined;

	const handleSelectTool = (tool: MCPTool) => {
		setSelectedTool(tool);
		setResult(null);
		// Initialiser les args avec des valeurs vides
		const initialArgs: Record<string, any> = {};
		if (tool.inputSchema?.properties) {
			Object.keys(tool.inputSchema.properties).forEach((key) => {
				// Pré-remplir project_id si un projet est déjà sélectionné
				if (key === 'project_id' && selectedProjectId) {
					initialArgs[key] = selectedProjectId;
				} else {
					initialArgs[key] = '';
				}
			});
		}
		setArgs(initialArgs);
	};

	const handleArgChange = (key: string, value: any) => {
		setArgs((prev) => ({ ...prev, [key]: value }));
	};

	const handleExecute = async () => {
		if (!selectedTool) return;

		setExecuting(true);
		setResult(null);

		try {
			// Filtrer les args vides
			const cleanArgs: Record<string, any> = {};
			Object.entries(args).forEach(([key, value]) => {
				if (value !== '' && value !== null && value !== undefined) {
					// Essayer de parser les nombres
					const prop = selectedTool.inputSchema?.properties?.[key];
					if (prop?.type === 'integer' || prop?.type === 'number') {
						cleanArgs[key] = Number(value);
					} else if (prop?.type === 'boolean') {
						cleanArgs[key] = value === 'true' || value === true;
					} else {
						cleanArgs[key] = value;
					}
				}
			});

			const res = await callMCPTool(selectedTool.name, cleanArgs, selectedProjectId || undefined);
			setResult(res);

			if (res.success) {
				notify('Tool executed successfully', { type: 'success' });
			} else {
				notify('Tool execution failed', { type: 'warning' });
			}
		} catch (err: any) {
			setResult({ success: false, error: err.message });
			notify('Error executing tool', { type: 'error' });
		} finally {
			setExecuting(false);
		}
	};

	const handleClear = () => {
		setResult(null);
		if (selectedTool?.inputSchema?.properties) {
			const initialArgs: Record<string, any> = {};
			Object.keys(selectedTool.inputSchema.properties).forEach((key) => {
				initialArgs[key] = '';
			});
			setArgs(initialArgs);
		}
	};

	const getToolIcon = (tool: MCPTool) => {
		if (tool.annotations?.destructiveHint) {
			return <WarningIcon color="error" fontSize="small" />;
		}
		if (tool.annotations?.readOnlyHint) {
			return <CheckCircleIcon color="success" fontSize="small" />;
		}
		return <BuildIcon color="primary" fontSize="small" />;
	};

	const formatResult = (text: string) => {
		try {
			const parsed = JSON.parse(text);
			return JSON.stringify(parsed, null, 2);
		} catch {
			return text;
		}
	};

	if (loading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Card>
			<Title title="MCP Tool Tester" />
			<CardContent>
				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				<Grid container spacing={2}>
					{/* LEFT - Tools List */}
					<Grid item xs={12} md={4}>
						<Paper sx={{ height: 'calc(100vh - 200px)', overflow: 'auto' }}>
							<Box sx={{ p: 2, bgcolor: 'grey.100', borderBottom: 1, borderColor: 'divider' }}>
								<Typography variant="subtitle1" fontWeight="bold">
									Tools disponibles ({tools.length})
								</Typography>
							</Box>
							<List dense>
								{tools.map((tool) => (
									<ListItem key={tool.name} disablePadding>
										<ListItemButton
											selected={selectedTool?.name === tool.name}
											onClick={() => handleSelectTool(tool)}
										>
											<ListItemIcon sx={{ minWidth: 36 }}>
												{getToolIcon(tool)}
											</ListItemIcon>
											<ListItemText
												primary={tool.name}
												secondary={tool.annotations?.title}
												primaryTypographyProps={{
													variant: 'body2',
													fontFamily: 'monospace',
												}}
												secondaryTypographyProps={{
													variant: 'caption',
													noWrap: true,
												}}
											/>
										</ListItemButton>
									</ListItem>
								))}
							</List>
						</Paper>
					</Grid>

					{/* RIGHT - Tool Details & Execution */}
					<Grid item xs={12} md={8}>
						<Paper sx={{ p: 2, height: 'calc(100vh - 200px)', overflow: 'auto' }}>
							{!selectedTool ? (
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										height: '100%',
										color: 'text.secondary',
									}}
								>
									<Typography>Selectionne un tool dans la liste</Typography>
								</Box>
							) : (
								<>
									{/* Tool Header */}
									<Box sx={{ mb: 3 }}>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
											<Typography variant="h6" fontFamily="monospace">
												{selectedTool.name}
											</Typography>
											{selectedTool.annotations?.readOnlyHint && (
												<Chip label="Read-only" size="small" color="success" />
											)}
											{selectedTool.annotations?.destructiveHint && (
												<Chip label="Destructive" size="small" color="error" />
											)}
										</Box>
										<Typography variant="body2" color="text.secondary">
											{selectedTool.description}
										</Typography>
									</Box>

									<Divider sx={{ mb: 2 }} />

									{/* Project Selector */}
									<FormControl fullWidth size="small" sx={{ mb: 2 }}>
										<Autocomplete
											options={projects}
											getOptionLabel={(option) => `${option.name} (${option.folderId})`}
											value={projects.find((p) => p.folderId === selectedProjectId) || null}
											onChange={(_, newValue) => {
												setSelectedProjectId(newValue?.folderId || '');
												// Auto-fill project_id si le tool l'attend
												if (
													selectedTool.inputSchema?.properties?.project_id &&
													newValue
												) {
													handleArgChange('project_id', newValue.folderId);
												}
											}}
											renderInput={(params) => (
												<TextField
													{...params}
													label={toolRequiresProject ? 'Projet *' : 'Projet'}
													placeholder="Rechercher un projet..."
													error={toolRequiresProject && !selectedProjectId}
													helperText={
														toolRequiresProject && !selectedProjectId
															? 'Ce tool requiert un projet'
															: undefined
													}
												/>
											)}
										/>
									</FormControl>

									{/* Arguments Form */}
									{selectedTool.inputSchema?.properties &&
										Object.keys(selectedTool.inputSchema.properties).filter(
											(k) => k !== 'project_id'
										).length > 0 && (
											<Box sx={{ mb: 2 }}>
												<Typography variant="subtitle2" gutterBottom>
													Arguments
												</Typography>
												<Grid container spacing={2}>
													{Object.entries(selectedTool.inputSchema.properties)
														.filter(([key]) => key !== 'project_id') // Masquer project_id (géré par le sélecteur)
														.map(([key, prop]) => {
															const isRequired =
																selectedTool.inputSchema?.required?.includes(key);

															// Enum -> Select
															if (prop.enum) {
																return (
																	<Grid item xs={12} sm={6} key={key}>
																		<FormControl fullWidth size="small">
																			<InputLabel>
																				{key}
																				{isRequired && ' *'}
																			</InputLabel>
																			<Select
																				value={args[key] || ''}
																				label={`${key}${isRequired ? ' *' : ''}`}
																				onChange={(e) =>
																					handleArgChange(key, e.target.value)
																				}
																			>
																				<MenuItem value="">
																					<em>Non defini</em>
																				</MenuItem>
																				{prop.enum.map((val) => (
																					<MenuItem key={val} value={val}>
																						{val}
																					</MenuItem>
																				))}
																			</Select>
																		</FormControl>
																	</Grid>
																);
															}

															// Boolean -> Select
															if (prop.type === 'boolean') {
																return (
																	<Grid item xs={12} sm={6} key={key}>
																		<FormControl fullWidth size="small">
																			<InputLabel>
																				{key}
																				{isRequired && ' *'}
																			</InputLabel>
																			<Select
																				value={
																					args[key] === true
																						? 'true'
																						: args[key] === false
																							? 'false'
																							: ''
																				}
																				label={`${key}${isRequired ? ' *' : ''}`}
																				onChange={(e) =>
																					handleArgChange(
																						key,
																						e.target.value === 'true'
																					)
																				}
																			>
																				<MenuItem value="">
																					<em>Non defini</em>
																				</MenuItem>
																				<MenuItem value="true">true</MenuItem>
																				<MenuItem value="false">false</MenuItem>
																			</Select>
																		</FormControl>
																	</Grid>
																);
															}

															// Default -> TextField
															return (
																<Grid item xs={12} sm={6} key={key}>
																	<TextField
																		fullWidth
																		size="small"
																		label={`${key}${isRequired ? ' *' : ''}`}
																		value={args[key] || ''}
																		onChange={(e) =>
																			handleArgChange(key, e.target.value)
																		}
																		placeholder={prop.description}
																		type={
																			prop.type === 'integer' ||
																			prop.type === 'number'
																				? 'number'
																				: 'text'
																		}
																		multiline={
																			key === 'sql' ||
																			key === 'description' ||
																			key === 'sql_template'
																		}
																		rows={
																			key === 'sql' ||
																			key === 'description' ||
																			key === 'sql_template'
																				? 3
																				: 1
																		}
																		helperText={prop.description}
																	/>
																</Grid>
															);
														}
													)}
												</Grid>
											</Box>
										)}

									{/* Actions */}
									<Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
										<Button
											variant="contained"
											color="primary"
											startIcon={
												executing ? (
													<CircularProgress size={20} color="inherit" />
												) : (
													<PlayArrowIcon />
												)
											}
											onClick={handleExecute}
											disabled={executing || (toolRequiresProject && !selectedProjectId)}
										>
											{executing ? 'Execution...' : 'Executer'}
										</Button>
										<Button
											variant="outlined"
											startIcon={<DeleteIcon />}
											onClick={handleClear}
											disabled={executing}
										>
											Reset
										</Button>
									</Box>

									{/* Result */}
									{result && (
										<Box>
											<Typography variant="subtitle2" gutterBottom>
												Resultat
											</Typography>
											<Paper
												sx={{
													p: 2,
													bgcolor: result.success ? 'grey.50' : 'error.50',
													border: 1,
													borderColor: result.success ? 'success.main' : 'error.main',
												}}
											>
												<Box
													sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
												>
													{result.success ? (
														<CheckCircleIcon color="success" />
													) : (
														<ErrorIcon color="error" />
													)}
													<Typography
														variant="body2"
														color={result.success ? 'success.main' : 'error.main'}
														fontWeight="bold"
													>
														{result.success ? 'Success' : 'Error'}
													</Typography>
												</Box>
												<Paper
													sx={{
														p: 1.5,
														bgcolor: 'grey.900',
														color: 'grey.100',
														fontFamily: 'monospace',
														fontSize: '0.8rem',
														overflow: 'auto',
														maxHeight: 400,
														whiteSpace: 'pre-wrap',
														wordBreak: 'break-word',
													}}
												>
													{result.success
														? formatResult(result.result || '')
														: result.error}
												</Paper>
											</Paper>
										</Box>
									)}
								</>
							)}
						</Paper>
					</Grid>
				</Grid>
			</CardContent>
		</Card>
	);
};
